/** Progress: fat-lost and lean-held meters, weigh-in and waist entry with a date field
 *  so entries can be back-filled, the 7-day mean against the trajectory, and the
 *  checkpoint table. Section 7. */
import { useMemo, useState } from 'react';
import type { StoreApi } from './useStore.ts';
import { Banner, Meter } from './components.tsx';
import { PLAN, META } from '../plan/data.ts';
import { positionFor, dateForDayIndex, toIsoLocal, parseIsoLocal } from '../plan/dates.ts';
import { weighIns, waistEntries, meanWeightForWeek, bestMeanUpTo, MIN_WEIGH_INS_FOR_MEAN }
  from '../plan/weights.ts';
import { START_COMP, TARGET_COMP, latestComp, trajectoryFor, trailingMeanOn } from '../plan/derived.ts';

export function Progress({ api }: { api: StoreApi }) {
  const { state, durable } = api;
  const startDate = state.get<string>('setting:startDate') ?? META.defaultStartDate;
  const today = positionFor(startDate, new Date());
  const entries = useMemo(() => weighIns(state), [state]);
  const waists = useMemo(() => waistEntries(state), [state]);
  const current = latestComp(state);

  return (
    <>
      <h1>Progress</h1>

      <h2>Body composition</h2>
      <div className="card">
        {current === null ? (
          <Banner kind="warn" title="Unknown">
            No weigh-in with a body-fat figure has been logged, so fat and lean mass cannot
            be computed. The app will not estimate them: a guessed body-fat number would
            make both meters below lie.
          </Banner>
        ) : (
          <>
            <Meter label="Fat lost" unit="kg" dp={2}
                   done={Math.max(0, START_COMP.fatKg - current.fatKg)}
                   total={START_COMP.fatKg - TARGET_COMP.fatKg} />
            <div style={{ height: 12 }} />
            <div className="row">
              <span className="k">Lean mass</span>
              <span className={`v ${current.leanKg >= START_COMP.leanKg ? 'ok' : 'bad'}`}>
                {current.leanKg.toFixed(2)} kg
                {' '}({current.leanKg >= START_COMP.leanKg ? '+' : ''}
                {(current.leanKg - START_COMP.leanKg).toFixed(2)} vs start)
              </span>
            </div>
            <div className="row">
              <span className="k">Fat mass</span>
              <span className="v">{current.fatKg.toFixed(2)} kg (target {TARGET_COMP.fatKg.toFixed(2)})</span>
            </div>
            <div className="row">
              <span className="k">Latest weigh-in</span>
              <span className="v">{current.weightKg.toFixed(2)} kg at {current.bodyFatPct.toFixed(1)}%</span>
            </div>
            <p className="sub" style={{ marginBottom: 0 }}>
              Lean mass has to <strong>rise</strong> by about 0.2 kg over the block, not merely
              hold — 4.50 kg of fat must go while total mass drops only 4.30 kg.
            </p>
          </>
        )}
      </div>

      <h2>Weigh-ins</h2>
      <EntryForm api={api} kind="weight" startDate={startDate} todayIso={toIsoLocal(new Date())} />

      <div className="card">
        <Chart state={state} startDate={startDate} entries={entries} currentWeek={today.week} />
      </div>

      <h2>Waist</h2>
      <EntryForm api={api} kind="waist" startDate={startDate} todayIso={toIsoLocal(new Date())} />
      {waists.length > 0 && (
        <div className="card">
          {waists.map((w) => (
            <div className="row" key={w.date}>
              <span className="k">{w.date}</span>
              <span className="v">
                {w.cm.toFixed(1)} cm
                {w.date !== waists[0].date &&
                  <span className="dim"> ({(w.cm - waists[0].cm).toFixed(1)} from baseline)</span>}
              </span>
            </div>
          ))}
        </div>
      )}

      <h2>Checkpoint trajectory</h2>
      <div className="card">
        <div className="row"><span className="k"><strong>Week</strong></span>
          <span className="v"><strong>Target · threshold · your mean</strong></span></div>
        {PLAN.gates.map((g) => {
          const mean = meanWeightForWeek(state, startDate, g.week);
          const best = bestMeanUpTo(state, startDate, g.week);
          const auto = g.crit.find((c) => c.auto !== undefined)!.auto!;
          const met = best !== null && best <= auto;
          return (
            <div className="row" key={g.lvl}>
              <span className="k">{g.week} — {g.name}</span>
              <span className="v">
                {trajectoryFor(g.week).weightKg.toFixed(2)} · {auto.toFixed(1)} ·{' '}
                <span className={met ? 'ok' : mean === null ? 'warn' : ''}>
                  {mean === null ? 'unknown' : mean.toFixed(2)}
                </span>
              </span>
            </div>
          );
        })}
        <p className="sub" style={{ marginBottom: 0 }}>
          A weekly mean needs at least {MIN_WEIGH_INS_FOR_MEAN} weigh-ins to count, so a single
          light morning can never clear a gate.
        </p>
      </div>

      {entries.length > 0 && (
        <>
          <h2>All weigh-ins</h2>
          <div className="card">
            {[...entries].reverse().map((w) => (
              <div className="row" key={w.date}>
                <span className="k">{w.date}</span>
                <span className="v">
                  {w.kg.toFixed(2)} kg{typeof w.bf === 'number' ? ` · ${w.bf.toFixed(1)}%` : ''}
                  {' '}<button className="linkbtn" disabled={!durable}
                              onClick={() => void api.del(`weight:${w.date}`)}>delete</button>
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

/** Weigh-in and waist entry. The date field defaults to today but is editable, so
 *  entries can be back-filled — which the spec asks for explicitly. */
function EntryForm({ api, kind, startDate, todayIso }: {
  api: StoreApi; kind: 'weight' | 'waist'; startDate: string; todayIso: string;
}) {
  const [date, setDate] = useState(todayIso);
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const key = `${kind}:${date}`;
  const pending = api.pending.has(key);

  const save = async () => {
    setNote(null);
    const nA = Number(a);
    if (!Number.isFinite(nA) || nA <= 0) { setNote(`Enter a ${kind === 'weight' ? 'weight' : 'measurement'}.`); return; }
    try { parseIsoLocal(date); } catch { setNote('That is not a valid date.'); return; }
    const value = kind === 'weight'
      ? (b.trim() === '' ? { kg: nA } : { kg: nA, bf: Number(b) })
      : { cm: nA };
    if (kind === 'weight' && b.trim() !== '' && !Number.isFinite(Number(b))) {
      setNote('Body fat must be a number, or left blank.'); return;
    }
    const ok = await api.put(key, value);
    setNote(ok ? `Saved ${date}.` : 'Not saved — the write did not return successfully.');
    if (ok) { setA(''); setB(''); }
  };

  const existing = api.state.get(key);
  const dayNo = (() => {
    try { return Math.round((parseIsoLocal(date).getTime() - parseIsoLocal(startDate).getTime()) / 86400000); }
    catch { return null; }
  })();

  return (
    <div className="card">
      <div className="entryrow">
        <label className="ef">
          <span className="label">Date</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label className="ef">
          <span className="label">{kind === 'weight' ? 'kg' : 'cm'}</span>
          <input type="text" inputMode="decimal" value={a} onChange={(e) => setA(e.target.value)}
                 placeholder={kind === 'weight' ? '76.4' : '86.5'} />
        </label>
        {kind === 'weight' && (
          <label className="ef">
            <span className="label">body fat %</span>
            <input type="text" inputMode="decimal" value={b} onChange={(e) => setB(e.target.value)}
                   placeholder="optional" />
          </label>
        )}
      </div>
      <div className="btns">
        <button className="primary" disabled={!api.durable || pending} onClick={() => void save()}>
          {pending ? 'Saving…' : existing !== undefined ? 'Overwrite this date' : 'Save'}
        </button>
      </div>
      <div className="sub" style={{ marginTop: 6 }}>
        {dayNo !== null && dayNo >= 0 && dayNo <= 111
          ? `Day ${dayNo + 1} of the plan${existing !== undefined ? ' · an entry already exists for this date' : ''}`
          : 'Outside the 16 weeks — still stored, but it will not count toward any weekly mean.'}
      </div>
      {note && <Banner kind={note.startsWith('Saved') ? 'ok' : 'bad'}>{note}</Banner>}
      {api.errorFor(key) && <Banner kind="bad" title="Not saved">{api.errorFor(key)}</Banner>}
    </div>
  );
}

/** 7-day mean against the trajectory, with individual weigh-ins as faint dots.
 *  Hand-drawn SVG: no chart library, so nothing to fail offline. */
function Chart({ startDate, entries, currentWeek }: {
  state: unknown; startDate: string; entries: Array<{ date: string; kg: number }>;
  currentWeek: number;
}) {
  const W = 320, H = 170, PAD_L = 34, PAD_B = 20, PAD_T = 8, PAD_R = 6;
  const days = 112;
  const all = [...PLAN.trajectory.weightKg, ...entries.map((e) => e.kg)];
  const lo = Math.floor(Math.min(...all) - 0.6);
  const hi = Math.ceil(Math.max(...all) + 0.6);
  const x = (day: number) => PAD_L + (day / days) * (W - PAD_L - PAD_R);
  const y = (kg: number) => PAD_T + (1 - (kg - lo) / (hi - lo)) * (H - PAD_T - PAD_B);

  const dayOf = (iso: string) =>
    Math.round((parseIsoLocal(iso).getTime() - parseIsoLocal(startDate).getTime()) / 86400000);

  const trajPath = PLAN.trajectory.weightKg
    .map((kg, w) => `${w === 0 ? 'M' : 'L'} ${x(w * 7).toFixed(1)} ${y(kg).toFixed(1)}`).join(' ');

  const meanPts = entries
    .map((e) => ({ day: dayOf(e.date), mean: trailingMeanOn(entries, e.date) }))
    .filter((p): p is { day: number; mean: number } => p.mean !== null && p.day >= 0 && p.day <= days)
    .sort((a, b) => a.day - b.day);
  const meanPath = meanPts.map((p, i) =>
    `${i === 0 ? 'M' : 'L'} ${x(p.day).toFixed(1)} ${y(p.mean).toFixed(1)}`).join(' ');

  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} className="chart" role="img"
           aria-label="Seven-day mean weight against the plan trajectory">
        {[lo, (lo + hi) / 2, hi].map((kg) => (
          <g key={kg}>
            <line x1={PAD_L} x2={W - PAD_R} y1={y(kg)} y2={y(kg)} className="grid" />
            <text x={4} y={y(kg) + 3.5} className="axis">{kg.toFixed(1)}</text>
          </g>
        ))}
        {[0, 4, 8, 12, 16].map((w) => (
          <text key={w} x={x(w * 7)} y={H - 6} className="axis" textAnchor="middle">{w}</text>
        ))}
        <path d={trajPath} className="traj" />
        {entries.map((e) => {
          const d = dayOf(e.date);
          if (d < 0 || d > days) return null;
          return <circle key={e.date} cx={x(d)} cy={y(e.kg)} r={2} className="dot" />;
        })}
        {meanPts.length > 1 && <path d={meanPath} className="meanline" />}
        <line x1={x(currentWeek * 7)} x2={x(currentWeek * 7)} y1={PAD_T} y2={H - PAD_B}
              className="nowline" />
      </svg>
      <div className="legend">
        <span><i className="sw traj" /> plan trajectory</span>
        <span><i className="sw meanline" /> your 7-day mean</span>
        <span><i className="sw dot" /> individual weigh-ins</span>
      </div>
      {entries.length === 0 && (
        <p className="sub" style={{ marginBottom: 0 }}>
          No weigh-ins logged yet, so only the plan line is shown.
        </p>
      )}
      {entries.length > 0 && meanPts.length === 0 && (
        <p className="sub" style={{ marginBottom: 0 }}>
          Weigh-ins are plotted, but no 7-day mean can be drawn yet — that needs at least{' '}
          {MIN_WEIGH_INS_FOR_MEAN} readings inside a seven-day window.
        </p>
      )}
    </>
  );
}

export { dateForDayIndex };
